import "./SendoutSessionDetailsScreen.scss";
import React, {useCallback, useEffect, useState} from "react";
import {ISendoutSession} from "../../../shared/models/sendout-session.model";
import CommonService from "../../../shared/services/common.service";
import {IAPIResponseType} from "../../../shared/models/api.model";
import {useNavigate, useParams} from "react-router-dom";
import StatusCardComponent from "../../../shared/components/status-card/statusCardComponent";
import ButtonComponent from "../../../shared/components/core/button/ButtonComponent";
import Grid from "@mui/material/Grid";
import DetailsDataKeyValueComponent
    from "../../../shared/components/details-data-key-value-component/detailsDataKeyValueComponent";
import {ITableColumn} from "../../../shared/models/table.model";
import CardComponent from "../../../shared/components/card/cardComponent";
import {Download} from "@mui/icons-material";
import {useDispatch, useSelector} from "react-redux";
import {setCurrentNavParams, setSideMenuView} from "../../../store/actions/header.action";
import {APIConfig, ImageConfig} from "../../../constants";
import {IRootReducerState} from "../../../store/reducers";
import TableWrapperV2Component
    from "../../../shared/components/table-components/table-wrapper-v2/TableWrapperV2Component";
import SearchComponent from "../../../shared/components/search/SearchComponent";
import LottieFileGenarationComponent
    from "../../../shared/components/core/lottie-file-genaration/LottieFileGenarationComponent";
import TooltipComponent from "../../../shared/components/tooltip/tooltipComponent";
import commonService from "../../../shared/services/common.service";

interface SendoutSessionDetailsScreenProps {

}

const SendoutSessionDetailsScreen = (props: SendoutSessionDetailsScreenProps) => {

    const {sessionID} = useParams();
    const navigate = useNavigate();
    const [sendoutSessionDetails, setSendoutSessionDetails] = useState<ISendoutSession | undefined>(undefined);
    const [isSendoutSessionDetailsLoading, setIsSendoutSessionDetailsLoading] = useState<boolean>(false);
    const [isSendoutSessionDetailsLoaded, setIsSendoutSessionDetailsLoaded] = useState<boolean>(false);
    const [isSendoutSessionDetailsLoadingFailed, setIsSendoutSessionDetailsLoadingFailed] = useState<boolean>(false);
    const [isCSVDownloading, setIsCSVDownloading] = useState(false);
    const {queryParamData} = useSelector((state: IRootReducerState) => state.stateManagement);
    const dispatch = useDispatch();
    const {currentUser} = useSelector((state: IRootReducerState) => state.account);

    const [filterData, setFilterData] = useState<any>({
        search: '',
        sendOutSessionId: sessionID
    });

    useEffect(() => {
        if (sessionID) {
            dispatch(setCurrentNavParams("Back", "Sendout", CommonService._routeConfig.SendoutSessionListRoute()));
            dispatch(setSideMenuView('compact'));
        }
    }, [sessionID, dispatch, navigate]);

    const handleSearch = useCallback((value: string) => {
        setFilterData((prevState: any) => ({
            ...prevState,
            search: value
        }));
        CommonService.setQueryParam('search', value);
    }, []);

    useEffect(() => {
        if (queryParamData) {
            setFilterData((prevState: any) => ({
                ...prevState,
                search: queryParamData.search || '',
            }));
        }
    }, [queryParamData]);

    const getSendOutSessionDetails = useCallback(() => {
        setIsSendoutSessionDetailsLoading(true);
        setIsSendoutSessionDetailsLoaded(false);
        setIsSendoutSessionDetailsLoadingFailed(false);
        CommonService._lab.getSendOutSessionDetails(sessionID)
            .then((response: IAPIResponseType<any>) => {
                setSendoutSessionDetails(response.data);
                setIsSendoutSessionDetailsLoading(false);
                setIsSendoutSessionDetailsLoaded(true);
                setIsSendoutSessionDetailsLoadingFailed(false);
            })
            .catch((error: any) => {
                setSendoutSessionDetails(undefined);
                setIsSendoutSessionDetailsLoading(false);
                setIsSendoutSessionDetailsLoaded(false);
                setIsSendoutSessionDetailsLoadingFailed(true);
            });
    }, [sessionID]);

    useEffect(() => {
        getSendOutSessionDetails();
    }, [sessionID, getSendOutSessionDetails]);

    const downloadCSV = useCallback(() => {
        const payload = {'sendoutId': sessionID};
        setIsCSVDownloading(true);
        CommonService._lab.downloadSendOutBatchCSV(payload)
            .then((response: IAPIResponseType<any>) => {
                setIsCSVDownloading(false);
                if (response && response?.data && response?.data?.attachmentUrl) {
                    CommonService.downloadFile(response?.data?.attachmentUrl, response?.data?.title || 'sendoutSessionDetailsList', 'csv');
                }
            })
            .catch((error: any) => {
                setIsCSVDownloading(false);
            });
    }, [sessionID]);

    const sendOutsColumns: ITableColumn[] = [
        {id: 'barcode', label: 'Accession ID', isDefaultColumn: true, fixed: "left", align: "center"},
        {id: 'sampleType', label: 'Sample Type', isDefaultColumn: true, align: "center"},
        {
            id: 'patient', label: 'Patient', isDefaultColumn: true, width: "250",
            content: (item: any) => (
                <>
                    {
                        item?.orderDetails?.patientDetails ? <>
                            {
                                (item?.orderDetails?.patientDetails?.firstName + ' ' + item?.orderDetails?.patientDetails?.lastName).length > 30 ?
                                    <TooltipComponent
                                        tooltip={CommonService.capitalizeFirstLetterOfEachWord(item?.orderDetails?.patientDetails?.firstName) + " " + CommonService.capitalizeFirstLetterOfEachWord(item?.orderDetails?.patientDetails?.lastName)}
                                        placement={"top"}
                                        arrow={true}
                                    >
                                        <div className={"ellipses-for-table-data"}>
                                            {CommonService.capitalizeFirstLetterOfEachWord(item?.orderDetails?.patientDetails?.firstName)} {CommonService.capitalizeFirstLetterOfEachWord(item?.orderDetails?.patientDetails?.lastName)}
                                        </div>
                                    </TooltipComponent> :
                                    <>
                                        {CommonService.capitalizeFirstLetterOfEachWord(item?.orderDetails?.patientDetails?.firstName)} {CommonService.capitalizeFirstLetterOfEachWord(item?.orderDetails?.patientDetails?.lastName)}
                                    </>
                            }
                            <br/>
                            {
                                item?.orderDetails?.patientDetails?.gender && <>
                                    (&nbsp;{item?.orderDetails?.patientDetails?.gender[0]?.toUpperCase()}&nbsp;)&nbsp;
                                </>
                            }
                            {commonService.formatDate(item?.orderDetails?.patientDetails.dateOfBirth)}
                        </> : <> - </>
                    }
                </>
            )
        },
        {id: 'testDetails.code', label: 'Test Code', isDefaultColumn: true, align: "center"},
        {
            id: 'tests', label: 'Ordered Test', isDefaultColumn: true, align: "center",
            content: (item: any) => (
                <>
                    {
                        item?.testDetails?.code
                    }
                </>
            )
        },
        {
            id: 'orderedDate', label: 'Collection Date & Time', width: "250", align: "center",
            isDefaultColumn: true,
            content: (item: any) => (
                <>
                    {item?.dateOfCollection ? commonService.formatDate(item.dateOfCollection) : "-"}
                    &nbsp;|&nbsp;
                    {item?.timeOfCollection ? item?.timeOfCollection.slice(0, 5) : "-"}
                </>
            )
        },
        {
            id: 'orderDetails.facilityDetails.name', label: 'Facility Name', isDefaultColumn: true, content: (item: any) => (
                <>
                    {item?.orderDetails.facilityDetails.name ? CommonService.capitalizeFirstLetterOfEachWord(item?.orderDetails.facilityDetails.name) : 'N/A'}
                </>
            )
        },
        {id: 'orderDetails.locationDetails.name', label: 'Location Name', isDefaultColumn: true, fixed: "right"},
    ];

    return (
        <div className={'send-out-session-details view-screen'}>
            <div className="view-details-wrapper">
                {
                    isSendoutSessionDetailsLoading && <div className="h-v-center">
                        <LottieFileGenarationComponent
                            loop={true}
                            autoplay={true}
                            animationData={ImageConfig.LoadingLottieFile}/>
                    </div>
                }
                {
                    isSendoutSessionDetailsLoadingFailed && <StatusCardComponent title={"Failed to load"}/>
                }
                {
                    isSendoutSessionDetailsLoaded && <>
                        <CardComponent className={'view-details-header-wrapper'}>
                            <div className="card-content">
                                <div className="view-details-card-header-button-wrapper">
                                    <div className="view-details-card-title">
                                        {
                                            sendoutSessionDetails?.labDetails?.name
                                        }
                                    </div>
                                    {/*<div className="view-details-options">*/}
                                    {/*    <ButtonComponent*/}
                                    {/*        className={"white-space-nowrap"}*/}
                                    {/*        type="button"*/}
                                    {/*        color={"primary"}*/}
                                    {/*        variant={"contained"}*/}
                                    {/*        fullWidth={false}*/}
                                    {/*        handleClick={() => {*/}
                                    {/*            navigate(CommonService._routeConfig.SendoutSessionListRoute())*/}
                                    {/*        }}*/}
                                    {/*    >*/}
                                    {/*        Back to Sendouts*/}
                                    {/*    </ButtonComponent>*/}
                                    {/*</div>*/}
                                </div>
                                <div className="view-details-content">
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={2}>
                                            <DetailsDataKeyValueComponent
                                                title={"Sendout Date"}>
                                                <div
                                                    className="view-details-value"> {commonService.formatDate(sendoutSessionDetails?.createdAt)} </div>
                                            </DetailsDataKeyValueComponent>
                                        </Grid>
                                        <Grid item xs={12} md={2}>
                                            <DetailsDataKeyValueComponent
                                                title={"Sendout Time"}>
                                                <div
                                                    className="details-value"> {commonService.formatTime(sendoutSessionDetails?.createdAt)} </div>
                                            </DetailsDataKeyValueComponent>
                                        </Grid>
                                        <Grid item xs={12} md={2}>
                                            <DetailsDataKeyValueComponent
                                                title={"Test Samples"}>
                                                <div
                                                    className="details-value"> {sendoutSessionDetails?.sampleCount}</div>
                                            </DetailsDataKeyValueComponent>
                                        </Grid>
                                        <Grid item xs={12} md={2}>
                                            <DetailsDataKeyValueComponent
                                                title={"Added by"}>
                                                <div
                                                    className="details-value">{sendoutSessionDetails?.createdByDetails?.firstName + " " + sendoutSessionDetails?.createdByDetails?.lastName}</div>
                                            </DetailsDataKeyValueComponent>
                                        </Grid>
                                    </Grid>
                                </div>
                            </div>
                        </CardComponent>
                        <div className="clear-fix"/>
                        <div className="send-outs-list-screen mrg-top-25 screen">
                            <div className={"screen-content-wrapper"}>
                                <div className="page-data-wrapper">
                                    <div className="page-options">
                                        <div className="page-search">
                                            <SearchComponent
                                                label={"Search Sendouts"}
                                                id="search_input"
                                                placeholder={""}
                                                value={filterData.search}
                                                onChange={handleSearch}
                                            />
                                        </div>
                                        <div className="page-extra-options">
                                            {
                                                commonService.checkAccess('SendOuts', "sendoutSampleCsv", currentUser?.userAccessModules) &&

                                                <ButtonComponent
                                                    type="button"
                                                    id="csv_btn"
                                                    color={"primary"}
                                                    variant={"outlined"}
                                                    fullWidth={false}
                                                    handleClick={downloadCSV}
                                                    disabled={isCSVDownloading}
                                                    startIcon={<Download/>}
                                                >
                                                    CSV
                                                </ButtonComponent>
                                            }&nbsp;&nbsp;
                                        </div>
                                    </div>
                                    <div className="page-data-wrapper t-table-inside-details">

                                        <TableWrapperV2Component
                                            url={APIConfig.GET_SEND_OUTS_SESSION_DETAILS_LIST.URL(sessionID)}
                                            method={APIConfig.GET_SEND_OUTS_SESSION_DETAILS_LIST.METHOD}
                                            isPaginated={true}
                                            columns={sendOutsColumns}
                                            extraPayload={filterData}
                                            isHoverable={true}
                                            isStickyHeader={true}
                                            isColumnFilterVisible={false}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                }
            </div>
        </div>
    );

};

export default SendoutSessionDetailsScreen;
